import { getApiUrl } from '../config/api';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authUtils } from '../utils/authUtils';

export default function ProtectedRoute({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get API URL at runtime
  const BACKEND_URL = getApiUrl();
  
  const [isAuthenticated, setIsAuthenticated] = useState(
    location.state?.user ? true : null
  );
  const [user, setUser] = useState(location.state?.user || null);

  // Fire-and-forget: tell the backend our browser timezone once per session.
  // Privacy-respectful — only the IANA timezone is sent (no IP, no precise
  // location). The backend resolves it to a country via the IANA database
  // and stamps the user doc. sessionStorage gate prevents redundant calls
  // on route changes within the same tab.
  const captureTimezone = React.useCallback(() => {
    try {
      if (sessionStorage.getItem('tz_captured')) return;
      const tz = Intl.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone;
      if (!tz) return;
      authUtils.authFetch(`${BACKEND_URL}/api/users/timezone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timezone: tz }),
      }).then(() => {
        sessionStorage.setItem('tz_captured', '1');
      }).catch(() => { /* non-critical */ });
    } catch (_) { /* environment without Intl — give up quietly */ }
  }, [BACKEND_URL]);
  
  useEffect(() => {
    // Skip auth check if user was passed from AuthCallback
    if (location.state?.user) {
      setIsAuthenticated(true);
      setUser(location.state.user);
      captureTimezone();
      return;
    }
    
    const checkAuth = async () => {
      try {
        const response = await authUtils.authFetch(`${BACKEND_URL}/api/auth/me`);
        
        if (!response.ok) {
          throw new Error('Not authenticated');
        }
        
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        captureTimezone();
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/', { replace: true });
      }
    };
    
    checkAuth();
  }, [navigate, location.state, captureTimezone]);
  
  // Loading state
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cosmos-bg to-cosmos-deep flex items-center justify-center">
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div
            className="w-16 h-16 mx-auto mb-6 rounded-full bg-etheric/30"
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.5, 0.8, 0.5]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            style={{
              boxShadow: '0 0 40px rgba(74, 111, 165, 0.3)'
            }}
          />
          <p className="text-starlight/70 font-heading">Verifying Field access...</p>
        </motion.div>
      </div>
    );
  }
  
  // Not authenticated
  if (isAuthenticated === false) {
    return null;
  }
  
  // Authenticated - render children with user in location state
  return React.cloneElement(children, { user });
}
